// packages/api/src/hooks/admin/useAnalysts.ts
//
// Phase A (2026-05-22) — Time de analise.
// 7 hooks de gerenciamento do roster + 2 selectors (options/me).
//
// Backend: /admin/analysts/* (api-alamo/src/modules/analyst-team).
// Permissions: leitura `proposals:analysis_read|proposals:analyze`; mutations
// `analyst_team:manage`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AddAnalystPayload,
  AnalystListFilters,
  AnalystOption,
  AnalystProfile,
  AnalystSupervisorOption,
  ApiSuccess,
  PauseAnalystPayload,
  UpdateAnalystPayload,
} from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

interface PaginatedAnalystsResponse {
  rows: AnalystProfile[];
  count: number;
  page: number;
  limit: number;
}

/** Lista paginada do time com filtros status/q + paginacao. */
export function useAnalysts(filters: AnalystListFilters = {}) {
  return useQuery({
    queryKey: queryKeys.analystTeam.list(filters as Record<string, unknown>),
    queryFn: async (): Promise<PaginatedAnalystsResponse> => {
      const response = await apiClient.get<ApiSuccess<PaginatedAnalystsResponse>>(
        '/admin/analysts',
        { params: filters },
      );
      return response.data.data;
    },
    staleTime: 60_000,
  });
}

/** Lista enxuta {userId, name} para popular selects. */
export function useAnalystsOptions() {
  return useQuery({
    queryKey: queryKeys.analystTeam.options(),
    queryFn: async (): Promise<AnalystOption[]> => {
      const response = await apiClient.get<ApiSuccess<AnalystOption[]>>('/admin/analysts/options');
      return response.data.data;
    },
    staleTime: 5 * 60_000,
  });
}

/** Lista membros classificados como supervisores para montar a hierarquia. */
export function useAnalystSupervisorOptions() {
  return useQuery({
    queryKey: queryKeys.analystTeam.supervisorOptions(),
    queryFn: async (): Promise<AnalystSupervisorOption[]> => {
      const response = await apiClient.get<ApiSuccess<AnalystSupervisorOption[]>>(
        '/admin/analysts/supervisor-options',
      );
      return response.data.data;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Retorna o perfil do usuário autenticado no time ou null.
 *
 * Phase A — usado pelo `AnalystQueuePage` pra decidir o default do filtro:
 * analista entra com `segment='mine'`; user com permissao mas sem profile
 * entra com `segment='all'` (admin/gerente que abre a fila como overview).
 */
export function useMyAnalystProfile(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.analystTeam.me(),
    queryFn: async (): Promise<AnalystProfile | null> => {
      const response = await apiClient.get<ApiSuccess<AnalystProfile | null>>('/admin/analysts/me');
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

/** Detalhe do analista por userId. */
export function useAnalyst(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.analystTeam.detail(userId ?? ''),
    enabled: Boolean(userId),
    queryFn: async (): Promise<AnalystProfile> => {
      const response = await apiClient.get<ApiSuccess<AnalystProfile>>(`/admin/analysts/${userId}`);
      return response.data.data;
    },
  });
}

export function useAddAnalyst() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AddAnalystPayload): Promise<AnalystProfile> => {
      const response = await apiClient.post<ApiSuccess<AnalystProfile>>('/admin/analysts', payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.analystTeam.all });
    },
  });
}

export function useUpdateAnalyst() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      payload: UpdateAnalystPayload;
    }): Promise<AnalystProfile> => {
      const response = await apiClient.patch<ApiSuccess<AnalystProfile>>(
        `/admin/analysts/${vars.userId}`,
        vars.payload,
      );
      return response.data.data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.analystTeam.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.analystTeam.detail(vars.userId),
      });
    },
  });
}

export function useRemoveAnalyst() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.delete(`/admin/analysts/${userId}`);
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.analystTeam.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.analystTeam.detail(userId),
      });
    },
  });
}

export function usePauseAnalyst() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (vars: {
      userId: string;
      payload: PauseAnalystPayload;
    }): Promise<AnalystProfile> => {
      const response = await apiClient.post<ApiSuccess<AnalystProfile>>(
        `/admin/analysts/${vars.userId}/pause`,
        vars.payload,
      );
      return response.data.data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.analystTeam.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.analystTeam.detail(vars.userId),
      });
    },
  });
}

export function useUnpauseAnalyst() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<AnalystProfile> => {
      const response = await apiClient.post<ApiSuccess<AnalystProfile>>(
        `/admin/analysts/${userId}/unpause`,
        {},
      );
      return response.data.data;
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.analystTeam.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.analystTeam.detail(userId),
      });
    },
  });
}

/**
 * Phase 18 Plan 03 (REQ-2) — Redistribui as proposal_assignments ativas do
 * analista via AutoAssignmentService round-robin, excluindo o proprio user
 * do pool candidato.
 *
 * Backend pode retornar:
 *   - 200 { releasedCount, redistributedCount, newAssignmentIds, unassignedCount }
 *   - 404 ANALYST_PROFILE_NOT_FOUND
 *
 * Sem capacidade, o backend libera a atribuicao e informa `unassignedCount`;
 * o scanner recorrente retoma a fila quando surgir vaga.
 */
export function useRedistributeAnalyst() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (vars: {
      userId: string;
    }): Promise<{
      releasedCount: number;
      redistributedCount: number;
      newAssignmentIds: string[];
      unassignedCount: number;
    }> => {
      const response = await apiClient.post<
        ApiSuccess<{
          releasedCount: number;
          redistributedCount: number;
          newAssignmentIds: string[];
          unassignedCount: number;
        }>
      >(`/admin/analysts/${vars.userId}/redistribute`, {});
      return response.data.data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.analystTeam.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.analystTeam.detail(vars.userId),
      });
      // War Room heatmap reflete novo workload por analista — invalidate
      // forca refetch do snapshot (Plan 18 Phase 12 useWarRoomSnapshot).
      queryClient.invalidateQueries({ queryKey: queryKeys.warRoom.all });
    },
  });
}
